import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, FolderOpen, BookOpen, ArrowRight, Zap } from 'lucide-react';
import AuthContext from '../context/AuthContext';
import BrandMark from '../components/BrandMark';

function WelcomePage() {
  const { user } = useContext(AuthContext);
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      <div className="max-w-2xl w-full text-center">
        {/* Welcome Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <BrandMark size={80} className="auth-logo" />
              <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-rebel-cyan flex items-center justify-center animate-pulse">
                <Sparkles className="w-4 h-4 text-black" />
              </div>
            </div>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            Welcome, <span className="text-gradient">{firstName}</span>
          </h1>
          <p className="text-xl text-gray-400">
            Your account is ready. Here's how to get the most out of Rebel Prompts.
          </p>
        </div>

        {/* Quick Start Cards */}
        <div className="grid gap-4 mb-8">
          <div className="card-glass p-6 text-left hover:border-rebel-cyan/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-rebel-cyan/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-rebel-cyan" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Create Your First Prompt</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Build a reusable prompt with variables. Use {'{{variable}}'} syntax to make inputs dynamic.
                </p>
                <Link to="/prompts/create" className="text-rebel-cyan text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Create a prompt <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="card-glass p-6 text-left hover:border-rebel-blue/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-rebel-blue/20 flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-6 h-6 text-rebel-blue" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Organize with Projects</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Group prompts into projects and folders. Keep everything structured as you scale.
                </p>
                <Link to="/projects" className="text-rebel-blue text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                  View projects <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>

          <div className="card-glass p-6 text-left hover:border-rebel-purple/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-rebel-purple/20 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-6 h-6 text-rebel-purple" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">Explore the Library</h3>
                <p className="text-gray-400 text-sm mb-3">
                  Browse community prompts. Fork anything you like and make it your own.
                </p>
                <Link to="/library" className="text-rebel-purple text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Browse library <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/prompts/create" className="btn-primary flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Start Creating
          </Link>
          <Link to="/profile" className="btn-secondary">
            Go to Profile
          </Link>
        </div>

        {/* Tips */}
        <div className="mt-12 p-4 rounded-xl bg-white/5 border border-white/10">
          <p className="text-sm text-gray-400">
            <span className="text-white font-medium">Pro tip:</span> Use keyboard shortcuts to work faster.
            Press <kbd className="px-2 py-0.5 rounded bg-white/10 text-xs mx-1">Alt + V</kbd> to quickly add variables when writing prompts.
          </p>
        </div>
      </div>
    </div>
  );
}

export default WelcomePage;
